import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  Typography,
  Button,
  Stack,
  Divider,
  IconButton,
  TextField,
  Autocomplete,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stepper,
  Step,
  StepLabel,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Checkbox,
  Chip,
  Alert,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  OutlinedInput,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import LockOpenIcon from "@mui/icons-material/LockOpen";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import ReceiptIcon from "@mui/icons-material/Receipt";
import DescriptionIcon from "@mui/icons-material/Description";

import useWorkspaceNavigation from "../../hooks/useWorkspaceNavigation";
import { useAppDispatch, useAppSelector } from "../../redux/store";
import {
  checkCashierHasOpenShift,
  getCurrentOrRecentShift,
} from "../../redux/actions/shiftActions";
import { getCompanyCharges } from "../../redux/actions/companyChargeActions";
import {
  getInvoices,
  getInvoiceById,
} from "../../redux/actions/invoiceActions";
import {
  getReceipts,
  getReceiptById,
} from "../../redux/actions/receiptActions";
import { getPaymentMethods } from "../../redux/actions/paymentMethodActions";
import {
  createCreditNote,
  resetCreditNoteServerStatus,
} from "../../redux/actions/creditNoteActions";
import DateRangeFilter from "../common/DateRangeFilter";
import FormAlert from "../common/FormAlert";
import AppButton from "../buttons/AppButton";
import { InvoiceSummaryDto } from "../../shared/dtos/invoice/InvoiceSummaryDto";
import { ReceiptSummaryDto } from "../../shared/dtos/receipt/ReceiptSummaryDto";
import { DocumentItemResponseDto } from "../../shared/dtos/document/DocumentItemResponseDto";
import { formatPlainNumber } from "../../utils/appUtils";
import { CreditNoteRequestDto } from "../../shared/dtos/creditNote/CreditNoteRequestDto";

interface CNoteItem {
  id?: string;
  itemId?: string;
  productId?: string;
  name: string;
  price: number;
  qty: number;
  discount: number;
  amount: number;
  creditedQuantity?: number;
  selected: boolean;
  returnQty: number;
  maxQty: number;
}

interface CNoteRefund {
  paymentMethodId: string;
  amount: string | number;
  referenceCode: string;
}

const CreateCreditNoteModal: React.FC<{
  open: boolean;
  onClose: () => void;
}> = ({ open, onClose }) => {
  const dispatch = useAppDispatch();
  const { navigateToTab } = useWorkspaceNavigation();
  const today = new Date().toISOString().split("T")[0];

  // --- REDUX STATES ---
  const { hasOpenShift, currentShift, loadingHasOpenShift } = useAppSelector(
    (state) => state.shift,
  );
  const {
    items: invoices,
    item: currentInvoice,
    loadingFetchAll: loadingInvoices,
  } = useAppSelector((state) => state.invoice);
  const {
    items: receipts,
    item: receipt,
    loadingFetchAll: loadingReceipts,
  } = useAppSelector((state) => state.receipt);
  const { items: paymentMethods } = useAppSelector(
    (state) => state.paymentMethod,
  );
  const { items: companyCharges } = useAppSelector(
    (state) => state.companyCharge,
  );
  const {
    loadingCreate: loadingCreateCreditNote,
    serverStatus,
    errorCreate: errorCreateCreditNote,
  } = useAppSelector((state) => state.creditNote);

  const safeCharges = useMemo(() => companyCharges || [], [companyCharges]);
  const paymentMethodOptions = useMemo(
    () =>
      (paymentMethods || []).map((pm) => ({ value: pm.id, label: pm.name })),
    [paymentMethods],
  );

  // --- WIZARD STATE ---
  const [activeStep, setActiveStep] = useState<number>(0);
  const [docType, setDocType] = useState<string>(""); // "invoice" or "receipt"
  const [selectedDoc, setSelectedDoc] = useState<
    InvoiceSummaryDto | ReceiptSummaryDto | null
  >(null);
  const [reason, setReason] = useState<string>("");
  const [items, setItems] = useState<CNoteItem[]>([]);
  const [chargeIds, setChargeIds] = useState<string[]>([]);
  const [refunds, setRefunds] = useState<CNoteRefund[]>([]);

  // Date Filters for Document Search
  const [startDate, setStartDate] = useState<string>(today);
  const [endDate, setEndDate] = useState<string>(today);

  // Derived Currency from selected document
  const docCurrency =
    selectedDoc?.currencyCode || selectedDoc?.currencyCode || "KES";

  // --- INITIALIZATION ---
  useEffect(() => {
    if (open) {
      dispatch(checkCashierHasOpenShift());
      dispatch(getPaymentMethods({ size: 20 }));
    }
  }, [dispatch, open]);

  useEffect(() => {
    if (open && hasOpenShift) {
      dispatch(getCurrentOrRecentShift());
      dispatch(getCompanyCharges());
    }
  }, [dispatch, open, hasOpenShift]);

  useEffect(() => {
    if (activeStep === 1) {
      if (docType === "invoice") {
        dispatch(getInvoices({ size: 20, startDate, endDate }));
      }
      if (docType === "receipt") {
        dispatch(getReceipts({ size: 20, startDate, endDate }));
      }
    }
  }, [docType, activeStep, startDate, endDate, dispatch]);

  useEffect(() => {
    if (selectedDoc && activeStep === 2) {
      if (docType === "invoice") dispatch(getInvoiceById(selectedDoc.id));
      if (docType === "receipt") dispatch(getReceiptById(selectedDoc.id));
    }
  }, [selectedDoc, activeStep, docType, dispatch]);

  // Extract items from selected document
  useEffect(() => {
    const details = docType === "invoice" ? currentInvoice : receipt;
    if (details && details.id === selectedDoc?.id) {
      setItems(
        (details.items || [])
          .map((i: DocumentItemResponseDto) => ({
            ...i,
            selected: false,
            returnQty: 1,
            maxQty: i.qty - (i.creditedQuantity || 0),
          }))
          .filter((i) => i.maxQty > 0),
      );
      setRefunds([{ paymentMethodId: "", amount: "", referenceCode: "" }]);
      setChargeIds([]);
    }
  }, [currentInvoice, receipt, docType, selectedDoc]);

  // Handle server success
  useEffect(() => {
    if (serverStatus?.operation === "create" && serverStatus?.status === 201) {
      dispatch(resetCreditNoteServerStatus());
      onClose();
    }
  }, [serverStatus, dispatch, onClose]);

  // --- HANDLERS ---
  const handleNext = (): void => setActiveStep((prev) => prev + 1);
  const handleBack = (): void => setActiveStep((prev) => prev - 1);

  const handleCreate = (): void => {
    const payload: CreditNoteRequestDto = {
      shiftId: currentShift?.id || "",
      originalDocumentId: selectedDoc!.id,
      issueDate: new Date().toISOString().split(".")[0] + "Z",
      reason,
      chargeIds,
      items: items
        .filter((i) => i.selected)
        .map((i) => ({
          itemId: i.productId!,
          qty: Number(i.returnQty),
          price: Number(i.price),
          discount: Number(i.discount || 0),
        })),
      refundPayments: refunds
        .filter((r) => Number(r.amount) > 0 && r.paymentMethodId)
        .map((r) => ({
          paymentMethodId: r.paymentMethodId,
          amount: Number(r.amount),
          referenceCode: r.referenceCode || null,
        })),
    };
    dispatch(createCreditNote(payload));
  };

  const handleAddRefund = (): void => {
    setRefunds([
      ...refunds,
      { paymentMethodId: "", amount: "", referenceCode: "" },
    ]);
  };

  const handleRemoveRefund = (index: number): void => {
    setRefunds(refunds.filter((_, i) => i !== index));
  };

  const handleRefundChange = (
    index: number,
    field: keyof CNoteRefund,
    value: string,
  ): void => {
    const newRefunds = [...refunds];
    newRefunds[index] = { ...newRefunds[index], [field]: value };
    setRefunds(newRefunds);
  };

  // --- FINANCIAL CALCULATIONS ---
  const grossReturnRaw = items
    .filter((i) => i.selected)
    .reduce((acc, i) => {
      const ratio = Number(i.returnQty) / Number(i.qty);
      const valueToReturn = Number(i.amount) * ratio;

      return acc + valueToReturn;
    }, 0);

  const grossReturn = Number(grossReturnRaw.toFixed(2));

  const { inclusivePenalties, exclusivePenalties } = chargeIds.reduce(
    (acc, id) => {
      const charge = safeCharges.find((c) => c.id === id);
      if (charge) {
        const amount = grossReturn * (Number(charge.rate || 0) / 100);
        if (charge.chargeType === "INCLUSIVE") acc.inclusivePenalties += amount;
        else acc.exclusivePenalties += amount;
      }
      return acc;
    },
    { inclusivePenalties: 0, exclusivePenalties: 0 },
  );

  const netRefundableRaw = grossReturn - exclusivePenalties;
  const netRefundable = Number(netRefundableRaw.toFixed(2));

  const totalRefunded = refunds.reduce(
    (acc, r) => acc + Number(r.amount || 0),
    0,
  );
  const balanceToStoreCredit = Number(
    (netRefundable - totalRefunded).toFixed(2),
  );

  // --- STEP RENDERS ---
  const renderStep1 = (): React.JSX.Element => (
    <Stack direction="row" spacing={3} sx={{ justifyContent: "center", py: 4 }}>
      <Button
        variant={docType === "invoice" ? "contained" : "outlined"}
        color="primary"
        sx={{
          width: 220,
          height: 140,
          display: "flex",
          flexDirection: "column",
        }}
        onClick={() => setDocType("invoice")}
      >
        <DescriptionIcon sx={{ fontSize: 40, mb: 1 }} />
        Return Invoice
      </Button>
      <Button
        variant={docType === "receipt" ? "contained" : "outlined"}
        color="primary"
        sx={{
          width: 220,
          height: 140,
          display: "flex",
          flexDirection: "column",
        }}
        onClick={() => setDocType("receipt")}
      >
        <ReceiptIcon sx={{ fontSize: 40, mb: 1 }} />
        Return Receipt
      </Button>
    </Stack>
  );

  type AnyDocSummary = InvoiceSummaryDto | ReceiptSummaryDto;

  const renderStep2 = (): React.JSX.Element => {
    const options: AnyDocSummary[] =
      docType === "invoice" ? invoices : receipts;
    const labelStr = docType === "invoice" ? "Invoice" : "Receipt";
    const loading = docType === "invoice" ? loadingInvoices : loadingReceipts;

    return (
      <Stack spacing={3} sx={{ py: 2 }}>
        <Box
          sx={{
            p: 2,
            bgcolor: "background.paper",
            borderRadius: 1,
            border: "1px solid",
            borderColor: "divider",
          }}
        >
          <Typography variant="subtitle2" gutterBottom>
            Filter by Date Range
          </Typography>
          <DateRangeFilter
            startDate={startDate}
            setStartDate={setStartDate}
            endDate={endDate}
            setEndDate={setEndDate}
          />
        </Box>

        <Autocomplete
          options={options || []}
          loading={loading}
          getOptionLabel={(opt) => {
            const documentNumber =
              "invoiceNumber" in opt
                ? opt.invoiceNumber
                : opt.transactionNumber;

            return `${documentNumber} - ${opt.customerName || "Walk-in"} (${docCurrency} ${formatPlainNumber(opt.total)})`;
          }}
          value={selectedDoc}
          onChange={(_e, val) => setSelectedDoc(val)}
          renderInput={(params) => (
            <TextField
              {...params}
              label={`Select Original ${labelStr}`}
              variant="outlined"
              color="secondary"
            />
          )}
        />
      </Stack>
    );
  };

  const renderStep3 = (): React.JSX.Element => (
    <Stack spacing={3} sx={{ py: 2 }}>
      <TextField
        fullWidth
        color="secondary"
        label="Reason for Credit Note"
        value={reason}
        onChange={(e) => setReason(e.target.value)}
        required
      />

      {/* ITEMS SELECTION */}
      <Box
        sx={{
          maxHeight: 250,
          overflowY: "auto",
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 1,
        }}
      >
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox"></TableCell>
              <TableCell>Item</TableCell>
              <TableCell align="center">Max</TableCell>
              <TableCell align="center">Return Qty</TableCell>
              <TableCell align="right">Unit Price</TableCell>
              <TableCell align="right">Total</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {items.map((item, idx) => (
              <TableRow key={idx}>
                <TableCell padding="checkbox">
                  <Checkbox
                    color="primary"
                    checked={item.selected}
                    onChange={(e) => {
                      const newItems = [...items];
                      newItems[idx].selected = e.target.checked;
                      setItems(newItems);
                    }}
                  />
                </TableCell>
                <TableCell>
                  <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                    {item.name}
                  </Typography>
                </TableCell>
                <TableCell align="center">
                  <Chip label={item.maxQty} size="small" />
                </TableCell>
                <TableCell align="center">
                  <TextField
                    type="number"
                    size="small"
                    disabled={!item.selected}
                    value={item.returnQty}
                    onChange={(e) => {
                      const newItems = [...items];
                      newItems[idx].returnQty = Math.max(
                        1,
                        Math.min(Number(e.target.value), item.maxQty),
                      );
                      setItems(newItems);
                    }}
                    sx={{ width: 70 }}
                  />
                </TableCell>
                <TableCell align="right">
                  {formatPlainNumber(item.price)}
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: "bold" }}>
                  {formatPlainNumber(
                    item.price * (item.selected ? item.returnQty : 0),
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Box>

      {/* PENALTIES / CHARGES */}
      <FormControl fullWidth size="small">
        <InputLabel>Apply Penalties/Charges (Optional)</InputLabel>
        <Select
          multiple
          value={chargeIds}
          onChange={(e) => setChargeIds(e.target.value as string[])}
          input={<OutlinedInput label="Apply Penalties/Charges (Optional)" />}
          renderValue={(selected) => (
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
              {(selected as string[]).map((val) => {
                const charge = safeCharges.find((c) => c.id === val);
                return (
                  <Chip
                    key={val}
                    label={`${charge?.name} (${charge?.rate}% - ${charge?.chargeType === "INCLUSIVE" ? "Incl." : "Excl."})`}
                    size="small"
                    onDelete={() =>
                      setChargeIds((prev) => prev.filter((id) => id !== val))
                    }
                    onMouseDown={(e) => e.stopPropagation()}
                  />
                );
              })}
            </Box>
          )}
        >
          {safeCharges.map((charge) => (
            <MenuItem key={charge.id} value={charge.id}>
              {charge.name} ({charge.rate}%) - {charge.chargeType}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <Divider />

      {/* REFUNDS SECTION */}
      <Box>
        <Typography variant="overline" color="text.secondary" gutterBottom>
          Process Refunds (Optional)
        </Typography>
        <Stack spacing={1} sx={{ mt: 1 }}>
          {refunds.map((refund, index) => (
            <Stack
              key={index}
              direction="row"
              spacing={1}
              sx={{ alignItems: "center" }}
            >
              <FormControl size="small" sx={{ flex: 2 }}>
                <InputLabel>Payment Method</InputLabel>
                <Select
                  value={refund.paymentMethodId}
                  label="Payment Method"
                  onChange={(e) =>
                    handleRefundChange(index, "paymentMethodId", e.target.value)
                  }
                >
                  {paymentMethodOptions.map((pm) => (
                    <MenuItem key={pm.value} value={pm.value}>
                      {pm.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                size="small"
                label="Amount"
                type="number"
                value={refund.amount}
                onChange={(e) =>
                  handleRefundChange(index, "amount", e.target.value)
                }
                sx={{ flex: 1 }}
              />
              <TextField
                size="small"
                label="Ref Code (Optional)"
                value={refund.referenceCode}
                onChange={(e) =>
                  handleRefundChange(index, "referenceCode", e.target.value)
                }
                sx={{ flex: 1 }}
              />
              <IconButton
                color="error"
                onClick={() => handleRemoveRefund(index)}
              >
                <DeleteIcon />
              </IconButton>
            </Stack>
          ))}
          <AppButton
            startIcon={<AddIcon />}
            onClick={handleAddRefund}
            sx={{ alignSelf: "flex-start" }}
          >
            Add Refund Method
          </AppButton>
        </Stack>
      </Box>

      {/* RECALCULATED TOTALS PANE */}
      <Box
        sx={{
          p: 2,
          bgcolor: "background.paper",
          borderRadius: 1,
          border: "1px solid",
          borderColor: "divider",
        }}
      >
        <Stack direction="row" sx={{ justifyContent: "space-between", mb: 1 }}>
          <Typography variant="body2" color="text.secondary">
            Gross Return Value:
          </Typography>
          <Typography variant="body2" sx={{ fontWeight: "500" }}>
            {docCurrency} {formatPlainNumber(grossReturn)}
          </Typography>
        </Stack>

        {exclusivePenalties > 0 && (
          <Stack
            direction="row"
            sx={{ justifyContent: "space-between", mb: 1 }}
          >
            <Typography variant="body2" color="error.main">
              Exclusive Penalties (-):
            </Typography>
            <Typography
              variant="body2"
              color="error.main"
              sx={{ fontWeight: "bold" }}
            >
              - {docCurrency} {formatPlainNumber(exclusivePenalties)}
            </Typography>
          </Stack>
        )}

        {inclusivePenalties > 0 && (
          <Stack
            direction="row"
            sx={{ justifyContent: "space-between", mb: 1 }}
          >
            <Typography variant="caption" color="text.secondary">
              ↳ Includes internal charges of {docCurrency}{" "}
              {formatPlainNumber(inclusivePenalties)}
            </Typography>
          </Stack>
        )}

        <Divider sx={{ my: 1.5 }} />

        <Stack
          direction="row"
          sx={{ justifyContent: "space-between", alignItems: "center", mb: 1 }}
        >
          <Typography variant="h6">Net Amount Refundable:</Typography>
          <Typography
            variant="h6"
            color="secondary"
            sx={{ fontWeight: "bold" }}
          >
            {docCurrency} {formatPlainNumber(netRefundable)}
          </Typography>
        </Stack>

        <Stack
          direction="row"
          sx={{ justifyContent: "space-between", alignItems: "center", mb: 1 }}
        >
          <Typography variant="body2">Total Processed Refund:</Typography>
          <Typography
            variant="body2"
            color="success.main"
            sx={{ fontWeight: "bold" }}
          >
            {docCurrency} {formatPlainNumber(totalRefunded)}
          </Typography>
        </Stack>

        {balanceToStoreCredit > 0 ? (
          <Alert severity="info" sx={{ mt: 1 }}>
            <strong>
              {docCurrency} {formatPlainNumber(balanceToStoreCredit)}
            </strong>{" "}
            will be retained as pending Store Credit for the customer.
          </Alert>
        ) : balanceToStoreCredit < 0 ? (
          <Alert severity="error" sx={{ mt: 1 }}>
            Refund amount exceeds the net refundable total by{" "}
            <strong>
              {docCurrency} {formatPlainNumber(Math.abs(balanceToStoreCredit))}
            </strong>
            .
          </Alert>
        ) : null}
      </Box>
    </Stack>
  );

  return (
    <Dialog
      open={open}
      onClose={loadingCreateCreditNote ? undefined : onClose}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle
        sx={{
          bgcolor: "background.paper",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: "bold" }}>
          Create Credit Note
        </Typography>
        <IconButton onClick={onClose} disabled={loadingCreateCreditNote}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ bgcolor: "background.default", p: 3 }}>
        {loadingHasOpenShift ? (
          <Typography>Checking shift status...</Typography>
        ) : !hasOpenShift ? (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              height: "50vh",
              p: 3,
            }}
          >
            <Alert severity="warning" sx={{ mb: 3 }}>
              An open shift is required to process credit notes and refunds.
            </Alert>
            <AppButton
              variant="contained"
              pill
              startIcon={<LockOpenIcon />}
              onClick={() => {
                onClose();
                navigateToTab("/shifts");
              }}
            >
              Go to Shifts Page
            </AppButton>
          </Box>
        ) : (
          <>
            <Box sx={{ width: "100%" }}>
              <FormAlert
                message={errorCreateCreditNote}
                severity="error"
                closable
                onClose={() => dispatch(resetCreditNoteServerStatus())}
              />
              <Stepper activeStep={activeStep} alternativeLabel sx={{ mb: 3 }}>
                <Step>
                  <StepLabel>Select Type</StepLabel>
                </Step>
                <Step>
                  <StepLabel>Choose Document</StepLabel>
                </Step>
                <Step>
                  <StepLabel>Items & Refund</StepLabel>
                </Step>
              </Stepper>
              {activeStep === 0 && renderStep1()}
              {activeStep === 1 && renderStep2()}
              {activeStep === 2 && renderStep3()}
            </Box>
          </>
        )}
      </DialogContent>

      {hasOpenShift && (
        <DialogActions sx={{ p: 2, bgcolor: "background.paper" }}>
          <AppButton pill variant="outlined" color="error" onClick={onClose}>
            Cancel
          </AppButton>
          <AppButton
            pill
            color="warning"
            onClick={handleBack}
            disabled={activeStep === 0 || loadingCreateCreditNote}
          >
            Back
          </AppButton>
          {activeStep < 2 ? (
            <AppButton
              pill
              variant="contained"
              color="primary"
              onClick={handleNext}
              disabled={activeStep === 0 ? !docType : !selectedDoc}
            >
              Next
            </AppButton>
          ) : (
            <AppButton
              variant="contained"
              color="primary"
              pill
              onClick={handleCreate}
              disabled={
                loadingCreateCreditNote ||
                !reason ||
                !items.some((i) => i.selected) ||
                netRefundable < 0 ||
                balanceToStoreCredit < 0
              }
            >
              {loadingCreateCreditNote ? "Processing..." : "Create Note"}
            </AppButton>
          )}
        </DialogActions>
      )}
    </Dialog>
  );
};

export default CreateCreditNoteModal;
